import { TextboxDialogData } from "@/shared/textbox-dialog.component";
import { GridReadyEvent } from "@ag-grid-community/core";
import { Location } from "@angular/common";
import { Component, OnInit, ViewEncapsulation } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import {
  ProxSubmission,
  ProxSubmissionStatus,
  ProxSubmissionStatusEnum,
} from "app/model/entities/entity-model";
import { AgFns, GridOptionsExt, ISortModel } from "app/shared/ag-fns";
import { DateFns } from "app/shared/date-fns";
import { ProximityBatchResult } from "app/shared/db-save.service";
import {
  DomainBaseComponent,
  wrapBusy,
} from "app/shared/domain-base.component";
import { DomainService } from "app/shared/domain.service";
import { environment } from "environments/environment";
import { takeUntil } from "rxjs/operators";

@Component({
  selector: "proximity-api",
  templateUrl: "./proximity-api.component.html",

  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class ProximityApiComponent
  extends DomainBaseComponent
  implements OnInit {
  currentTabLabel: string;

  afterDate: Date;
  shouldShowPendingOnly: boolean;
  shouldShowErrorsOnly: boolean;

  bulkSubGridOptions: GridOptionsExt;
  cmbSubGridOptions: GridOptionsExt;
  soloSubGridOptions: GridOptionsExt;

  totalRemainingCount: number;

  currentTabIx: number = 0;
  tabIxBulk = 0;
  tabIxCmb = 1;

  constructor(
    protected domainService: DomainService,
    protected route: ActivatedRoute,
    protected location: Location
  ) {
    super(domainService);

    this.route.paramMap.pipe(takeUntil(this.onDestroy)).subscribe(() => {
      this.updateFromContext();
    });
  }

  async updateFromContext() {
    var x = this.dbQueryService.getAllCached(ProxSubmissionStatus);

    this.bulkSubGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onBulkSubGridReady,
      onModelUpdated: (p) => { }, // need to suppress automatic autosize that occurs here.
      getRowHeight: undefined, // // we want autoHeight for this grid
      rowModelType: "serverSide",
      tooltipShowDelay: 0,
    });

    this.cmbSubGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onCmbSubGridReady,
      onModelUpdated: (p) => { }, // need to suppress automatic autosize that occurs here.
      rowModelType: "serverSide",
      getRowHeight: undefined, // // we want autoHeight for this grid
      tooltipShowDelay: 0,
    });

    this.totalRemainingCount =
      await this.dbQueryService.getProxSubmissionsPendingCount();

    this.currentTabLabel = "Bulk";

    this.afterDate = DateFns.dateAdd(new Date(), -2, "weeks");

    this.isPageReady = true;
  }

  override async canDeactivate() {
    const ok = await super.canDeactivate();
    if (!ok) return false;
    this.uow.clearEntities(ProxSubmission);
    return true;
  }

  async onBulkSubGridReady(event: GridReadyEvent) {
    const width = (w: number) => {
      return { minWidth: w, maxWidth: w };
    };
    const colDefs = [
      {
        headerName: "Table Name",
        field: "tableName",
        filter: "agTextColumnFilter",
        ...width(220),
      },
      {
        headerName: "Entity Id 1",
        field: "entityId1",
        filter: "agTextColumnFilter",
        ...width(100),
      },
      {
        headerName: "Entity Id 2",
        field: "entityId2",
        filter: "agTextColumnFilter",
        ...width(100),
      },
      {
        ...AgFns.createButtonProps("", this.getEntityDescription.bind(this), {
          label: "View",
        }),
        ...width(80),
      },
      {
        headerName: "Description",
        field: "_entityDescription",
        filter: "agTextColumnFilter",
      },

      {
        ...AgFns.createCheckboxProps("Is Delete", "isDelete"),
        editable: false,
      },

      { headerName: "Created On", field: "crtnTs", ...width(120) },

      { headerName: "Submitted On", field: "submissionTs", ...width(120) },
      {
        headerName: "Status",
        field: "proxSubmissionStatus.name",
        ...width(120),
      },
      {
        colId: "Allow Resubmit",
        ...AgFns.createButtonProps(
          "",
          this.allowResubmit.bind(this),
          {
            label: "Allow Resubmit",
            canDisplay: this.canAllowResubmit.bind(this),
          }
        ),
        minWidth: 170,
      },
      {
        headerName: "Error",
        field: "error",
        filter: "agTextColumnFilter",
        tooltipField: "error",
      },
      // { headerName: 'Submission Id', field: 'proxSubmissionId', filter: 'agNumberColumnFilter', ...width(100) },
    ];

    const sortModel = [{ colId: "crtnTs", sort: "desc" }] as ISortModel;

    AgFns.initGrid(event, colDefs, sortModel);
    this.updateBulkSubDatasource();

    this.bulkSubGridOptions.apiX.resetRowHeights();
  }

  updateBulkSubDatasource() {
    const gridApi = this.bulkSubGridOptions && this.bulkSubGridOptions.apiX;
    if (gridApi == null) {
      return;
    }

    let q = this.dbQueryService.createProxSubmissionQuery(
      this.afterDate,
      this.shouldShowErrorsOnly,
      this.shouldShowPendingOnly,
      false
    );

    const ds = AgFns.buildDatasource(() => q);
    gridApi.setGridOption("serverSideDatasource", ds);
  }

  async onCmbSubGridReady(event: GridReadyEvent) {
    const width = (w: number) => {
      return { minWidth: w, maxWidth: w };
    };
    const colDefs = [
      {
        headerName: "Table Name",
        field: "tableName",
        filter: "agTextColumnFilter",
        ...width(220),
      },
      {
        headerName: "From Entity Id",
        field: "entityId1",
        filter: "agTextColumnFilter",
        ...width(100),
      },
      {
        headerName: "To Entity Id",
        field: "entityId2",
        filter: "agTextColumnFilter",
        ...width(100),
      },
      { headerName: "Created On", field: "crtnTs", ...width(120) },
      { headerName: "Submitted On", field: "submissionTs", ...width(120) },
      {
        headerName: "Status",
        field: "proxSubmissionStatus.name",
        ...width(120),
      },
      {
        colId: "Allow Resubmit",
        ...AgFns.createButtonProps(
          "",
          this.allowResubmit.bind(this),
          {
            label: "Allow Resubmit",
            canDisplay: this.canAllowResubmit.bind(this),
          }
        ),
        minWidth: 170,
      },
      {
        headerName: "Error",
        field: "error",
        filter: "agTextColumnFilter",
        tooltipField: "error",
      },
    ];

    const sortModel = [{ colId: "crtnTs", sort: "desc" }] as ISortModel;

    AgFns.initGrid(event, colDefs, sortModel);
    this.updateCmbSubDatasource();
    this.cmbSubGridOptions.apiX.resetRowHeights();
  }

  updateCmbSubDatasource() {
    const gridApi = this.cmbSubGridOptions && this.cmbSubGridOptions.apiX;
    if (gridApi == null) {
      return;
    }

    let q = this.dbQueryService.createProxSubmissionQuery(
      this.afterDate,
      this.shouldShowErrorsOnly,
      this.shouldShowPendingOnly,
      true
    );

    const ds = AgFns.buildDatasource(() => q);
    gridApi.setGridOption("serverSideDatasource", ds);
  }

  async onTabChanged(evt: any) {
    this.currentTabIx = evt.index;
    this.refreshQuery();
  }

  async refreshQuery() {
    if (this.currentTabIx == this.tabIxBulk) {
      this.updateBulkSubDatasource();
    } else {
      this.updateCmbSubDatasource();
    }
    this.bulkSubGridOptions.apiX.refreshServerSide();
    this.cmbSubGridOptions.apiX.refreshServerSide();
  }

  async getEntityDescription(item: ProxSubmission) {
    let descr = "na";
    if (item.isCombination) {
      const descr1 = await this.dbQueryService.getEntityDescription(
        item.tableName,
        item.entityId1.toString()
      );
      const descr2 = await this.dbQueryService.getEntityDescription(
        item.tableName,
        item.entityId2.toString()
      );
      descr = "From: " + descr1 + " To: " + descr2;
    } else {
      descr = await this.dbQueryService.getEntityDescription(
        item.tableName,
        item.entityId1.toString(),
        item.entityId2.toString()
      );
    }

    item["_entityDescription"] = descr;
    this.bulkSubGridOptions.apiX.refreshCells();
  }

  canAllowResubmit(item: ProxSubmission) {
    return (
      item.error != null &&
      item.proxSubmissionStatusId != ProxSubmissionStatusEnum.TimeoutError
    );
  }

  async allowResubmit(item: ProxSubmission) {
    item.error = null;

    item.proxSubmissionStatusId = ProxSubmissionStatusEnum.NotYetSent;

    await this.dbSaveService.saveSelectedChanges([item]);

    this.bulkSubGridOptions.apiX.refreshCells();
    this.cmbSubGridOptions.apiX.refreshCells();
    this.totalRemainingCount =
      await this.dbQueryService.getProxSubmissionsPendingCount();
  }

  @wrapBusy
  async submitSupplierData() {
    // Put up toast during long updates.
    this.toastr.success("Sending");
    const intervalId = setInterval(() => {
      this.dbQueryService.getProxSubmissionsPendingCount().then((count) => {
        this.totalRemainingCount = count;
        this.toastr.success(`${count} Items Remaining `);
      });
    }, 4000);
    clearInterval(intervalId);

    var shouldContinue = true;
    var r: ProximityBatchResult;
    while (shouldContinue) {
      r = await this.dbSaveService.sendProximitySupplierDataByBatch();
      shouldContinue = r.Ok && r.Count > 0;
    }

    if (r.Ok) {
      this.toastr.success("Send Complete - OK");
    } else {
      const msg = JSON.stringify(r, null, 2);
      await this.dialogService.showOkMessage(
        "Proximity API Error",
        r.ErrorMessage
      );
    }

    this.totalRemainingCount =
      await this.dbQueryService.getProxSubmissionsPendingCount();

    this.refreshQuery();
  }

  get isSystemAdmin() {
    return this.user.isSystemAdmin;
  }

  async getApiVersion() {
    var version = await this.dbQueryService.getProximityApiVersion();
    await this.dialogService.okDialog("Proximity API Version", version);
  }

  async generateToken() {
    const url = environment.apiRoot + 'api/Login/GetApiToken';
    const pretext = 'Copy the value in the box below, and include it in an Authorization header in all calls to the Uniformax API.' +
      '\n\nTo save the token in Proximity, go to the Supplier API page and click "Add Supplier Token"';
    const response = await this.dbSaveService.postData<{ token }>(url, {});
    const config: TextboxDialogData = {
      pretext,
      text: response.token,
      posttext: 'Protect this token as a password!  It grants access to Job Orders in Uniformax.',
    };
    await this.domainService.dialogService.textboxDialog('Bearer Token', config);
  }

  async addToken() {
    const pretext = 'Paste the Proximity token in the box below.  It will authenticate Uniformax to the Proximity API.' +
      '\n\nTo get the token from Proximity, go to the Supplier API page and click the "Generate Token" button.';
    const config: TextboxDialogData = {
      writable: true,
      pretext,
      text: '',
      posttext: 'Protect this token as a password!  It grants access to your supplier data in Proximity.',
    };
    const token = await this.dialogService.textboxDialog('Bearer Token', config);
    if (token && token.length > 100 && token.startsWith("ey")) {
      const body = { value: token }
      const response = await this.dbSaveService.postData<{ Value: string }>("UpdateToken", body);
      await this.dialogService.okDialog("Bearer Token", response.Value);
    } else if (token) {
      await this.dialogService.okDialog("Bearer Token", "Invalid token provided");
    }
  }

}
